import React, { useState, useEffect } from 'react';

const SegmentManager = ({ segments = [], games = [], onSegmentsChange, contentId = null }) => {
  const [localSegments, setLocalSegments] = useState(segments);
  const [showAddSegment, setShowAddSegment] = useState(false);
  const [newSegment, setNewSegment] = useState({
    gameId: '',
    durationMinutes: '',
    avgCcv: '',
    peakCcv: ''
  });

  // Update local segments when prop changes (e.g., from auto-detection)
  useEffect(() => {
    setLocalSegments(segments);
  }, [segments]);

  const addSegment = () => {
    if (!newSegment.gameId || !newSegment.durationMinutes) {
      alert('Please select a game and enter duration');
      return;
    }

    const segment = {
      ...newSegment,
      durationMinutes: parseInt(newSegment.durationMinutes),
      avgCcv: parseInt(newSegment.avgCcv) || null,
      peakCcv: parseInt(newSegment.peakCcv) || null,
      segmentOrder: localSegments.length + 1,
      tempId: Date.now(),
      isManual: true
    };

    const updatedSegments = [...localSegments, segment];
    setLocalSegments(updatedSegments);
    onSegmentsChange(updatedSegments);

    setNewSegment({ gameId: '', durationMinutes: '', avgCcv: '', peakCcv: '' });
    setShowAddSegment(false);
  };

  const removeSegment = (index) => {
    const updatedSegments = localSegments.filter((_, i) => i !== index);
    updatedSegments.forEach((seg, i) => seg.segmentOrder = i + 1);
    setLocalSegments(updatedSegments);
    onSegmentsChange(updatedSegments);
  };

  const getGameName = (gameId) => {
    const game = games.find(g => g.id === gameId);
    return game?.title || 'Unknown Game';
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const formatStartTime = (seconds) => {
    if (!seconds) return '0:00';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const totalDuration = localSegments.reduce((sum, seg) => sum + (parseInt(seg.durationMinutes) || 0), 0);
  const autoDetectedCount = localSegments.filter(s => !s.isManual).length;

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h4 className="font-medium text-gray-900">🎮 Game Segments</h4>
          <p className="text-sm text-gray-500">
            {autoDetectedCount > 0 
              ? `✨ ${autoDetectedCount} segments auto-detected from the stream`
              : 'Track different games played in this stream/video'
            }
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowAddSegment(!showAddSegment)}
          className="bg-sc-pink text-white px-3 py-1 rounded text-sm hover:bg-sc-pink/90"
        >
          + Add Segment
        </button>
      </div>

      {/* Auto-detected banner */}
      {autoDetectedCount > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 text-sm text-green-800">
          ✨ <strong>{autoDetectedCount} game segments</strong> were automatically detected from this VOD. 
          You can edit or remove them as needed.
        </div>
      )}

      {/* Add Segment Form */}
      {showAddSegment && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
          <h5 className="font-medium mb-3">Add Game Segment</h5>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="col-span-2">
              <label className="block text-xs text-gray-600 mb-1">Game *</label>
              <select
                value={newSegment.gameId}
                onChange={(e) => setNewSegment({ ...newSegment, gameId: e.target.value })}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
              >
                <option value="">Select game...</option>
                {games.map((g) => (
                  <option key={g.id} value={g.id}>{g.title}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">Duration (mins) *</label>
              <input
                type="number"
                value={newSegment.durationMinutes}
                onChange={(e) => setNewSegment({ ...newSegment, durationMinutes: e.target.value })}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                placeholder="90"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">Avg CCV</label>
              <input
                type="number"
                value={newSegment.avgCcv}
                onChange={(e) => setNewSegment({ ...newSegment, avgCcv: e.target.value })}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                placeholder="25000"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">Peak CCV</label>
              <input
                type="number"
                value={newSegment.peakCcv}
                onChange={(e) => setNewSegment({ ...newSegment, peakCcv: e.target.value })}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                placeholder="35000"
              />
            </div>

            <div className="col-span-2 md:col-span-3 flex items-end gap-2">
              <button
                type="button"
                onClick={addSegment}
                className="bg-green-600 text-white px-4 py-1.5 rounded text-sm hover:bg-green-700"
              >
                Add Segment
              </button>
              <button
                type="button"
                onClick={() => setShowAddSegment(false)}
                className="bg-gray-300 text-gray-700 px-4 py-1.5 rounded text-sm hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Segments List */}
      {localSegments.length === 0 ? (
        <div className="text-center py-6 text-gray-500">
          <p>No segments added yet.</p>
          <p className="text-sm">Segments auto-detect from Twitch VODs and live Kick/Twitch category changes, or add manually.</p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {localSegments.map((segment, index) => (
              <div 
                key={segment.id || segment.tempId || index} 
                className={`bg-white border rounded-lg p-3 flex items-center justify-between ${
                  segment.isManual ? 'border-gray-200' : 'border-green-200'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <span className="text-gray-400 font-mono text-sm">#{index + 1}</span>
                    {segment.startTimeSeconds > 0 && (
                      <p className="text-xs text-gray-400">{formatStartTime(segment.startTimeSeconds)}</p>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900">
                        {segment.gameName || segment.game?.title || getGameName(segment.gameId)}
                      </p>
                      {!segment.isManual && (
                        <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                          auto
                        </span>
                      )}
                    </div>
                    <div className="flex gap-4 text-sm text-gray-500">
                      <span>⏱️ {formatDuration(segment.durationMinutes)}</span>
                      {segment.avgCcv && <span>👥 {segment.avgCcv.toLocaleString()} avg</span>}
                      {segment.peakCcv && <span>📈 {segment.peakCcv.toLocaleString()} peak</span>}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeSegment(index)}
                  className="text-red-500 hover:text-red-700 p-1"
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>

          {/* Total Duration */}
          <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
            <span className="text-sm text-gray-600">Total Tracked Duration:</span>
            <span className="font-bold text-gray-900">{formatDuration(totalDuration)}</span>
          </div>
        </>
      )}
    </div>
  );
};

export default SegmentManager;
